import { useEffect, useMemo, useState } from 'react';
import { Mail, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import Layout from '../components/Layout';
import toast from 'react-hot-toast';
import styles from './NotasFiscaisPage.module.css';
import type { CodigoServico, CreateCodigoServicoDto, UpdateCodigoServicoDto } from '../services/codigosServicoService';
import { codigosServicoService } from '../services/codigosServicoService';
import { lancamentosService } from '../services/lancamentosService';

const STORAGE_KEY = 'sagex:emailFinanceiro';

export default function NotasFiscaisPage() {
  const [items, setItems] = useState<CodigoServico[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Email do setor financeiro
  const [emailFinanceiro, setEmailFinanceiro] = useState<string>(
    () => localStorage.getItem(STORAGE_KEY) || ''
  );
  const [emailInput, setEmailInput] = useState<string>(
    () => localStorage.getItem(STORAGE_KEY) || ''
  );
  const [savingEmail, setSavingEmail] = useState(false);

  const [atualizandoStatus, setAtualizandoStatus] = useState(false);

  const handleAtualizarStatus = async () => {
    if (!confirm('Atualizar status de todos os lançamentos agora? Esta ação afeta o banco de dados em produção.')) return;
    setAtualizandoStatus(true);
    try {
      const resultado = await lancamentosService.atualizarStatus();
      toast.success(
        `Concluído: ${resultado.atualizadosParaAtrasado} atrasados, ${resultado.atualizadosParaPendente} pendentes, ${resultado.atualizadosParaPago} pagos, ${resultado.atualizadosParaNaoFaturado} não faturados (de ${resultado.totalAnalisados} analisados)`,
        { duration: 6000 },
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao atualizar status dos lançamentos');
    } finally {
      setAtualizandoStatus(false);
    }
  };

  const handleSaveEmail = () => {
    const trimmed = emailInput.trim();
    if (trimmed && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Informe um e-mail válido');
      return;
    }
    setSavingEmail(true);
    setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, trimmed);
      setEmailFinanceiro(trimmed);
      setSavingEmail(false);
      toast.success(trimmed ? 'E-mail salvo com sucesso' : 'E-mail removido');
    }, 400);
  };

  const [createForm, setCreateForm] = useState<CreateCodigoServicoDto>({
    codigoAtividade: '',
    codigoServico: '',
    descricao: '',
    tipo: '',
    ativo: true,
  });

  const editingItem = useMemo(
    () => items.find((i) => i.id === editingId) || null,
    [items, editingId],
  );

  const [editForm, setEditForm] = useState<UpdateCodigoServicoDto>({});

  const load = async () => {
    setLoading(true);
    try {
      const data = await codigosServicoService.list();
      setItems(data);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar codigos de servico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!editingItem) {
      setEditForm({});
      return;
    }
    setEditForm({
      codigoAtividade: editingItem.codigoAtividade,
      codigoServico: editingItem.codigoServico,
      descricao: editingItem.descricao,
      tipo: editingItem.tipo || '',
      ativo: editingItem.ativo,
    });
  }, [editingItem]);

  const handleCreate = async () => {
    if (!createForm.codigoAtividade.trim() || !createForm.codigoServico.trim() || !createForm.descricao.trim()) {
      toast.error('Preencha codigo atividade, codigo servico e descricao');
      return;
    }

    setSaving(true);
    try {
      await codigosServicoService.create({
        codigoAtividade: createForm.codigoAtividade.trim(),
        codigoServico: createForm.codigoServico.trim(),
        descricao: createForm.descricao.trim(),
        tipo: createForm.tipo?.trim() || undefined,
        ativo: createForm.ativo ?? true,
      });
      toast.success('Codigo cadastrado');
      setCreateForm({
        codigoAtividade: '',
        codigoServico: '',
        descricao: '',
        tipo: '',
        ativo: true,
      });
      await load();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Erro ao cadastrar codigo');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    if (!editForm.codigoAtividade?.trim() || !editForm.codigoServico?.trim() || !editForm.descricao?.trim()) {
      toast.error('Preencha codigo atividade, codigo servico e descricao');
      return;
    }

    setSaving(true);
    try {
      await codigosServicoService.update(editingId, {
        codigoAtividade: editForm.codigoAtividade.trim(),
        codigoServico: editForm.codigoServico.trim(),
        descricao: editForm.descricao.trim(),
        tipo: editForm.tipo?.trim() || undefined,
        ativo: editForm.ativo,
      });
      toast.success('Codigo atualizado');
      setEditingId(null);
      await load();
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Erro ao atualizar codigo');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Excluir este codigo de servico?')) return;
    setSaving(true);
    try {
      await codigosServicoService.remove(id);
      toast.success('Codigo excluido');
      if (editingId === id) setEditingId(null);
      await load();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao excluir codigo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout title="Notas Fiscais">
        <div className={styles.pageContainer}>Carregando...</div>
      </Layout>
    );
  }

  return (
    <Layout title="Notas Fiscais">
      <div className={styles.pageContainer}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Notas Fiscais</h1>
            <p className={styles.subtitle}>CRUD de codigos de servico usados na emissao de NFSe (Bling).</p>
          </div>
        </div>

        {/* ── Configurações de E-mail ─────────────────── */}
        <div className={styles.card}>
          <div className={styles.cardTitleRow}>
            <Mail size={18} className={styles.cardTitleIcon} />
            <span className={styles.cardTitle}>Configurações de E-mail</span>
          </div>
          <p className={styles.cardDesc}>
            E-mail do setor financeiro que receberá cópias das notas fiscais e relatórios.
          </p>
          <div className={styles.emailRow}>
            <label className={styles.labelWide}>
              E-mail do setor financeiro
              <input
                id="emailFinanceiro"
                className={styles.input}
                type="email"
                placeholder="financeiro@escola.com.br"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSaveEmail()}
              />
            </label>
            <button
              className={styles.primaryButton}
              onClick={handleSaveEmail}
              disabled={savingEmail || emailInput === emailFinanceiro}
            >
              <Save size={16} />
              {savingEmail ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
          {emailFinanceiro && (
            <p className={styles.emailSaved}>
              ✓ E-mail atual: <strong>{emailFinanceiro}</strong>
            </p>
          )}
        </div>

        {/* ── Manutenção ─────────────────────────────── */}
        <div className={styles.card}>
          <div className={styles.cardTitleRow}>
            <RefreshCw size={18} className={styles.cardTitleIcon} />
            <span className={styles.cardTitle}>Manutenção</span>
          </div>
          <p className={styles.cardDesc}>
            Atualiza o status de todos os lançamentos com base na data de vencimento. Roda automaticamente todo dia às 06h00, mas pode ser executado manualmente quando necessário.
          </p>
          <button
            className={styles.primaryButton}
            onClick={handleAtualizarStatus}
            disabled={atualizandoStatus}
          >
            <RefreshCw size={16} className={atualizandoStatus ? styles.spinning : undefined} />
            {atualizandoStatus ? 'Atualizando...' : 'Atualizar status dos lançamentos'}
          </button>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTitle}>Cadastrar codigo</div>
          <div className={styles.formGrid}>
            <label className={styles.label}>
              Codigo Atividade
              <input
                className={styles.input}
                value={createForm.codigoAtividade}
                onChange={(e) => setCreateForm((p) => ({ ...p, codigoAtividade: e.target.value }))}
              />
            </label>
            <label className={styles.label}>
              Codigo Servico
              <input
                className={styles.input}
                value={createForm.codigoServico}
                onChange={(e) => setCreateForm((p) => ({ ...p, codigoServico: e.target.value }))}
              />
            </label>
            <label className={styles.label}>
              Tipo
              <input
                className={styles.input}
                value={createForm.tipo || ''}
                onChange={(e) => setCreateForm((p) => ({ ...p, tipo: e.target.value }))}
                placeholder="ex: ENSINO_FUNDAMENTAL"
              />
            </label>
            <label className={styles.label}>
              Ativo
              <select
                className={styles.input}
                value={(createForm.ativo ?? true) ? 'true' : 'false'}
                onChange={(e) => setCreateForm((p) => ({ ...p, ativo: e.target.value === 'true' }))}
              >
                <option value="true">Sim</option>
                <option value="false">Nao</option>
              </select>
            </label>
            <label className={styles.labelWide}>
              Descricao
              <input
                className={styles.input}
                value={createForm.descricao}
                onChange={(e) => setCreateForm((p) => ({ ...p, descricao: e.target.value }))}
              />
            </label>
          </div>
          <button className={styles.primaryButton} onClick={handleCreate} disabled={saving}>
            <Plus size={18} />
            {saving ? 'Salvando...' : 'Cadastrar'}
          </button>
        </div>

        <div className={styles.tableCard}>
          <div className={styles.tableHeader}>
            <div className={styles.cardTitle}>Codigos cadastrados</div>
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Atividade</th>
                  <th>Servico</th>
                  <th>Descricao</th>
                  <th>Tipo</th>
                  <th>Ativo</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    className={editingId === it.id ? styles.activeRow : undefined}
                    onClick={() => setEditingId(it.id)}
                  >
                    <td>{it.codigoAtividade}</td>
                    <td>{it.codigoServico}</td>
                    <td>{it.descricao}</td>
                    <td>{it.tipo || ''}</td>
                    <td>{it.ativo ? 'Sim' : 'Nao'}</td>
                    <td>
                      <button
                        className={styles.dangerIconButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(it.id);
                        }}
                        title="Excluir"
                        disabled={saving}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className={styles.emptyCell}>
                      Nenhum codigo cadastrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {editingItem && (
          <div className={styles.card}>
            <div className={styles.cardTitle}>Editar codigo (ID {editingItem.id})</div>
            <div className={styles.formGrid}>
              <label className={styles.label}>
                Codigo Atividade
                <input
                  className={styles.input}
                  value={editForm.codigoAtividade || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, codigoAtividade: e.target.value }))}
                />
              </label>
              <label className={styles.label}>
                Codigo Servico
                <input
                  className={styles.input}
                  value={editForm.codigoServico || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, codigoServico: e.target.value }))}
                />
              </label>
              <label className={styles.label}>
                Tipo
                <input
                  className={styles.input}
                  value={editForm.tipo || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, tipo: e.target.value }))}
                />
              </label>
              <label className={styles.label}>
                Ativo
                <select
                  className={styles.input}
                  value={(editForm.ativo ?? true) ? 'true' : 'false'}
                  onChange={(e) => setEditForm((p) => ({ ...p, ativo: e.target.value === 'true' }))}
                >
                  <option value="true">Sim</option>
                  <option value="false">Nao</option>
                </select>
              </label>
              <label className={styles.labelWide}>
                Descricao
                <input
                  className={styles.input}
                  value={editForm.descricao || ''}
                  onChange={(e) => setEditForm((p) => ({ ...p, descricao: e.target.value }))}
                />
              </label>
            </div>
            <div className={styles.actionsRow}>
              <button className={styles.secondaryButton} onClick={() => setEditingId(null)} disabled={saving}>
                Cancelar
              </button>
              <button className={styles.primaryButton} onClick={handleSaveEdit} disabled={saving}>
                <Save size={18} />
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
